/**
 * **O sinal da Garra Hemorrágica, num sítio só.**
 *
 * ## Porque um CARÁCTER e não um polígono
 *
 * Porque o dono pediu um sinal japonês que signifique quebrado ou ferido, e um
 * kanji aproximado à mão não é um kanji: seria um rabisco que se parece com
 * escrita, e para quem lê japonês seria um erro visível. Ou se desenha o
 * carácter real, ou se desenha outra coisa e não se lhe chama um sinal.
 *
 * ## Os dois caracteres, e porque são dois
 *
 * - **傷** (*kizu*) — ferida. É o que fica NO INIMIGO: uma marca é uma ferida
 *   aberta que ainda não foi cobrada, e ela vive sobre o casco atingido.
 * - **破** (*ha*) — romper, rasgar. É o que o JOGADOR faz: o botão não mostra uma
 *   ferida, mostra a ação de a rebentar.
 *
 * Um só carácter para as duas coisas apagaria a diferença entre o estado e a
 * ordem, que é a distinção em que este gate inteiro assenta.
 *
 * ## Uma função, e não uma textura partilhada
 *
 * Uma primeira versão rasterizava o 傷 numa textura do gestor e CARIMBAVA-a N
 * vezes numa `RenderTexture` por alvo. O carimbo não desenha nada no Phaser
 * 4.2.1 - ver a nota em `bleed-mark.ts` - e o que substitui a `RenderTexture` é
 * uma textura de canvas por alvo, onde o carácter é escrito diretamente.
 *
 * Então o que este módulo guarda é o que é mesmo partilhado: **o carácter, o
 * tipo de letra e a forma de o desenhar**. Nenhum alvo escolhe uma fonte, e o
 * dia em que o sinal mudar há um sítio para o mudar.
 */

/**
 * A pilha de tipos de letra, com CJK coberto em cada sistema que este jogo
 * corre.
 *
 * A ordem é deliberada: as três primeiras são as que o Windows traz e têm o
 * carácter; `sans-serif` é o último recurso e num sistema sem CJK nenhum
 * produziria um retângulo vazio - o que é feio, e é honesto, e é preferível a
 * escolher outro símbolo por medo.
 */
const GLYPH_FONT = '"Yu Gothic", "Meiryo", "MS Gothic", "Noto Sans JP", sans-serif';

/** **傷** - ferida. O sinal que fica no inimigo enquanto a marca existir. */
export const BLEED_MARK_GLYPH = '傷';

/** **破** - romper. O sinal da ORDEM, no botão que as rebenta. */
export const BLEED_BURST_GLYPH = '破';

/**
 * Quanto da caixa reservada o carácter ocupa.
 *
 * Abaixo de um, para os traços que saem da caixa tipográfica - e um kanji
 * tem-nos - não serem cortados pela borda. Um carácter cortado lê-se como um
 * carácter diferente.
 */
const GLYPH_FILL = 0.82;

/**
 * O contexto 2D mínimo que este módulo escreve.
 *
 * Estreito de propósito: o que aqui se faz é escolher um tipo de letra e
 * escrever um carácter, e um tipo estreito é o que permite prová-lo sem abrir um
 * canvas de verdade.
 */
export interface GlyphContext {
  font: string;
  fillStyle: string | CanvasGradient | CanvasPattern;
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
  shadowColor: string;
  shadowBlur: number;
  clearRect(x: number, y: number, width: number, height: number): void;
  fillText(text: string, x: number, y: number): void;
}

/**
 * **Escreve o carácter centrado em `(x, y)`, com `size` píxeis de caixa.**
 *
 * ## Porque BRANCO, quando a marca é vermelha
 *
 * Porque a cor é aplicada por `setTint` na `Image` que mostra a camada, e o
 * `tint` do WebGL MULTIPLICA. Um carácter já vermelho tingido de vermelho fica
 * castanho-escuro e ilegível; um carácter branco aceita qualquer cor exatamente
 * como ela é - o carmesim da marca parada e o quase-branco do instante em que
 * ela rompe, do mesmo desenho.
 */
export function drawBleedGlyph(
  context: GlyphContext,
  glyph: string,
  x: number,
  y: number,
  size: number,
): void {
  context.font = `700 ${Math.round(size * GLYPH_FILL)}px ${GLYPH_FONT}`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  /*
   * Um halo escuro por baixo do traço, e não um contorno.
   *
   * Um contorno de kanji fecha os vãos internos do carácter em tamanhos
   * pequenos e transforma-o numa mancha. A sombra escurece o que está POR TRÁS,
   * portanto o traço continua a ler-se contra um casco claro sem lhe engrossar
   * a forma.
   */
  context.shadowColor = 'rgba(0, 0, 0, 0.85)';
  /*
   * Discreto. Um desfoque generoso enche os vãos INTERNOS do carácter de escuro
   * e é metade da razão pela qual ele se lia como uma mancha - o que se quer é
   * separá-lo do fundo, não engrossá-lo.
   */
  context.shadowBlur = Math.max(1, Math.round(size * 0.06));
  context.fillStyle = '#ffffff';
  context.fillText(glyph, x, y);
}
